import React, { useEffect, useState } from "react";
import {
  Dialog,
  Toolbar,
  IconButton,
  Typography,
  Button,
  DialogContent,
  Box,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { DateRangePicker } from "rsuite";
import ViewTransactionsRanges, {
  Loader,
} from "../HelperFunctions/HelperFunctions";
import "rsuite/dist/rsuite.min.css";
import ViewAndDisplayViewer from "./ViewAndDisplayViewer";
import { useLoaderData } from "react-router-dom";
import { useFormikContext } from "formik";
import {
  getCorporations,
  getPCsByCorporation,
  LoadRestaurantCorporationList,
  RestGetPortifoliosWithBrands,
} from "../API/nimbleAPI";
import RestaurantCorpSelectDialog, {
  normalizeRestaurantCorpRows,
} from "./RestaurantCorpSelectDialog";
import dayjs from "dayjs";

const REPORT_KEY = "Restaurant Flash Report";
const transactionRanges = ViewTransactionsRanges({});

const toDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (dayjs.isDayjs(value)) return value.toDate();
  return new Date(value);
};

const sectionSx = {
  border: "1px solid #d0d0d0",
  borderRadius: 1,
  p: 1.5,
};

const normalizePortfolios = (data) => {
  const list = Array.isArray(data)
    ? data
    : data?.Groups || data?.Portfolios || data?.ListInfo || data?.Data || [];

  return list.map((item) => ({
    ...item,
    DisplayText:
      item.DisplayText ||
      item.Value ||
      item.Name ||
      item.GroupName ||
      item.PortfolioName ||
      "",
    ValueCode: item.ValueCode ?? item.Key ?? item.GroupID ?? item.ID,
  }));
};

const RestuarentCustomization = ({ open, onClose, corpCount }) => {

    const { values, setFieldValue } = useFormikContext();

    const selectedCorporationCount =
    values.selected_corporations?.length || 0;

  const { userPreferences, corpId } = useLoaderData();
  const saved = values.customization?.[REPORT_KEY] || {};

  const [loading, setLoading] = useState(false);
  const [reportType, setReportType] = useState("Single");
  const [corporation, setCorporation] = useState(null);
  const [viewTransactions, setViewTransactions] = useState("Today");
  const [corporations, setCorporations] = useState([]);
  const [profitCenters, setProfitCenters] = useState([]);
  const [profitCenter, setProfitCenter] = useState(null);
  const [selectedCorporations, setSelectedCorporations] = useState([]);
  const [corpDialog, setCorpDialog] = useState(false);
  // const [corporationCount, setCorporationCount] = useState(0);
  const [restaurantCorps, setRestaurantCorps] = useState([]);
  const [restPortifolios, setRestPortifolios] = useState([]);
  const [viewDisplayData, setViewDisplayData] = useState({
    metrics: {},
    comparison: {},
    display: {},
  });
  const [exportFormat, setExportFormat] = useState("Excel");
  const [dateRange, setDateRange] = useState([
    toDate(dayjs().startOf("day")),
    toDate(dayjs().endOf("day")),
  ]);

  const isMultiple = reportType === "Combined";
  const exportOptions = [
    { value: "Excel", label: "Excel" },
    { value: "PDF", label: "PDF" },
  ];

  const onDateChange = (dates) => {
    if (!dates) return;
    setDateRange(dates);
    setViewTransactions("Range");
  };

  const onViewChange = (val) => {
    const match = transactionRanges.find((r) => r.date === val);
    setViewTransactions(val);
    if (!match?.actualValue?.start || !match?.actualValue?.end) return;
    setDateRange([
      toDate(match.actualValue.start),
      toDate(match.actualValue.end),
    ]);
  };

  useEffect(() => {
    if (!open) return;

    const s = values.customization?.[REPORT_KEY] || {};
    const liveCorps = (values.selected_corporations || []).map(String);
    setReportType(s.reportType || "Single");
    setViewTransactions(s.viewTransactions || "Today");
    const savedExport =
      s.export ||
      s.exportType ||
      s.export_type ||
      s.layout_settings?.export ||
      "Excel";
    setExportFormat(
      String(savedExport).toLowerCase() === "pdf" ? "PDF" : "Excel",
    );
    setSelectedCorporations(
      s.reportType === "Combined"
        ? liveCorps.length
          ? liveCorps
          : (s.CorpIds || []).map(String)
        : [],
    );
    if (s.FromDate && s.ToDate) {
      setDateRange([new Date(s.FromDate), new Date(s.ToDate)]);
    }
  }, [open]);

  useEffect(() => {
    if (!corporations.length) return;

    const id = saved.CorpIds?.[0] ?? corpId;
    const corp = corporations.find(
      (c) => String(c.ID) === String(id) && c.ID !== "select",
    );
    if (corp) setCorporation(corp);
  }, [corporations, corpId, saved.CorpIds]);


 
  const selectedCorpLabel =
    selectedCorporations.length === corpCount && corpCount > 0
      ? "All_corporation(s)selected"
      : selectedCorporations.length > 9
        ? `${selectedCorporations.length}_corporation(s)selected`
        : selectedCorporations.length > 0
          ? `0${selectedCorporations.length}_corporation(s)selected`
          : "00_corporation(s)selected";

  const setSelect = userPreferences?.isLegalName
    ? { ID: "select", LegalName: "-Select-", AccountTypeName: "" }
    : { ID: "select", Name: "-Select-", AccountTypeName: "" };

  useEffect(() => {
    if (!open || isMultiple) return;

    const loadCorporations = async () => {
      try {
        setLoading(true);
        const data = await getCorporations(setSelect);

        const updatedCorporations = (data || []).map((corp) => ({
          ...corp,
          displayName:
            corp.ID === "select"
              ? "-Select-"
              : userPreferences?.IsShowCorpLegalName
                ? corp.LegalName
                : corp.Name,
        }));
        if(corpId === "000000000000000000000000000000000000"){ 
          setCorporation(data[0]);
        }

        setCorporations(updatedCorporations);
      } catch (error) {
        console.error("Failed to load corporations:", error);
      } finally {
        setLoading(false);
      }
    };

    loadCorporations();
  }, [open, isMultiple]);

  useEffect(() => {
    if (!open || !isMultiple) return;

    const loadRestaurantData = async () => {
      try {
        setLoading(true);
        const [corps, portfolios] = await Promise.all([
          LoadRestaurantCorporationList(),
          RestGetPortifoliosWithBrands(),
        ]);
        const normalizedCorps = normalizeRestaurantCorpRows(corps);
        setRestaurantCorps(corps || []);
        corpCount?.(normalizedCorps.length);
         setRestPortifolios(normalizePortfolios(portfolios));
      } catch (error) {
        console.error("Failed to load restaurant corporations:", error);
        setRestaurantCorps([]);
        setRestPortifolios([]);
      } finally {
        setLoading(false);
      }
    };

    loadRestaurantData();
  }, [open, isMultiple]);

  const handleCorporationChange = async (_, value) => {
    setCorporation(value);
    setProfitCenter(null);

    if (!value?.ID) return;

    try {
      setLoading(true);
      const pcs = await getPCsByCorporation(value.ID);
      setProfitCenters(pcs?.filter((pc) => pc.ID !== "select") || []);
    } catch (error) {
      setProfitCenters([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = () => {
    // Corporations and date range come from the schedule (selected_corporations +
    // yesterday at fetch time), not from this customization screen.
    const payload = {
      reportType,
      viewTransactions,
      PCIDs: profitCenter?.ID ? [profitCenter.ID] : [],
      export: exportFormat,

      IsRevenue: !!viewDisplayData.metrics?.IsRevenue,
      IsCOGSAndExp: !!viewDisplayData.metrics?.IsCOGSAndExp,
      IsLabour: !!viewDisplayData.metrics?.IsLabour,
      IsOtherExpense: !!viewDisplayData.metrics?.IsOtherExpense,

      LY: !!viewDisplayData.comparison?.LY,
      LyAndVariance: !!viewDisplayData.comparison?.LyAndVariance,
      LyAndVariancePerctange:
        !!viewDisplayData.comparison?.LyAndVariancePerctange,

      viewType: viewDisplayData.metrics?.viewType,
      ShowZeroValue: !!viewDisplayData.display?.ShowZeroValue,
      IsNoSaleTrans: !!viewDisplayData.display?.IsNoSaleTrans,
      IsParentheses: !!viewDisplayData.display?.IsParentheses,
      IsTrailingMinus: !!viewDisplayData.display?.IsTrailingMinus,
      IsNegativeInRed: !!viewDisplayData.display?.IsNegativeInRed,
      IsWithoutCents: !!viewDisplayData.display?.IsWithoutCents,
    };

    setFieldValue("customization", {
      ...values.customization,
      [REPORT_KEY]: payload,
    });

    onClose();
  };
  return (
    <>
      <Dialog
        open={open}
        fullScreen
        disableEnforceFocus
        disableAutoFocus
        sx={{
          "& .MuiDialog-paper": {
            display: "flex",
            flexDirection: "column",
            height: "100%",
            overflow: "visible",
          },
        }}
        PaperProps={{
          elevation: 0,
          sx: { border: "1px solid rgba(0, 0, 0, 0.12)" },
        }}
      >
        <Toolbar sx={{ bgcolor: "#d7d7d7", flexShrink: 0 }}>
          <IconButton
            size="small"
            edge="start"
            onClick={onClose}
            aria-label="close"
            style={{ backgroundColor: "#686868" }}
            disableRipple
          >
            <CloseIcon sx={{ color: "white" }} />
          </IconButton>

          <Typography
            sx={{ ml: 2, flex: 1, color: "#1f1f1f" }}
            component="div"
            fontWeight={600}
            fontSize="16px"
          >
            Customizations
          </Typography>

          <Stack direction="row" spacing={2}>
            <Button
              size="small"
              disableRipple
              disableElevation
              variant="contained"
              sx={{
                px: 4,
                textTransform: "none",
                backgroundColor: "#b5dcf2",
                color: "#000",
              }}
              type="button"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              size="small"
              disableRipple
              disableElevation
              variant="contained"
              sx={{
                px: 4,
                textTransform: "none",
                backgroundColor: "#b5dcf2",
                color: "#000",
              }}
              type="button"
              onClick={handleSave}
            >
              Save
            </Button>
          </Stack>
        </Toolbar>

        <DialogContent
          id="restaurant-dialog-content"
          sx={{
            flex: 1,
            minHeight: 0,
            p: 3,
            overflow: "auto",
            position: "relative",
          }}
        >
          <Stack direction="row" gap={1} height="100%">
            <Box
              sx={{
                width: "30%",
                height: "100%",
                overflow: "auto",
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
              }}
            >
              {/* <Box sx={sectionSx}> */}
                {/* <Typography fontSize={13} color="#555" mb={0.5}>
                  Daily Flash Report
                </Typography> */}
                {/* <FormControl fullWidth size="small"> */}
                  {/* <Select
                    value={reportType}
                    onChange={(e) => {
                      const next = e.target.value;
                      setReportType(next);
                      setCorporation(null);
                      setProfitCenter(null);
                      setProfitCenters([]);
                      setRestaurantCorps([]);
                      setSelectedCorporations(
                        next === "Combined"
                          ? (values.selected_corporations || []).map(String)
                          : [],
                      );
                    }}
                  > */}
                    {/* <MenuItem value="Single">Single</MenuItem> */}
                    {/* <MenuItem value="Combined">Combined</MenuItem> */}
                  {/* </Select> */}
                {/* </FormControl> */}
              {/* </Box> */}

              <Box sx={{ ...sectionSx, flex: 1, minHeight: 0 }}>
                <Stack spacing={1.5}>
                  {/* {isMultiple ? (
                    <Box>
                      <Typography fontSize={13} color="#555">
                        Corporation
                      </Typography>
                      <TextField
                        size="small"
                        fullWidth
                        value={selectedCorpLabel}
                        onClick={() => setCorpDialog(true)}
                        InputProps={{ readOnly: true }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            cursor: "pointer",
                            fontSize: "13px",
                            bgcolor: "#f5f5f5",
                            fontWeight: 500,
                          },
                          "& .MuiOutlinedInput-input": {
                            cursor: "pointer",
                          },
                        }}
                      />
                    </Box>
                  ) : (
                    <>
                      <Box>
                        <Typography fontSize={13} color="#555">
                          Corporation
                        </Typography>
                        <Autocomplete
                          size="small"
                          options={corporations}
                          value={corporation}
                          onChange={handleCorporationChange}
                          getOptionLabel={(option) => option?.Name || ""}
                          isOptionEqualToValue={(option, value) =>
                            option?.ID === value?.ID
                          }
                          renderInput={(params) => <TextField {...params} />}
                        />
                      </Box>
                      {profitCenters.length > 0 && (
                        <Box>
                          <Typography fontSize={13} color="#555">
                            Profit Center
                          </Typography>
                          <Autocomplete
                            size="small"
                            options={profitCenters}
                            value={profitCenter}
                            onChange={(_, value) => setProfitCenter(value)}
                            getOptionLabel={(option) => option?.Name || ""}
                            isOptionEqualToValue={(option, value) =>
                              option?.ID === value?.ID
                            }
                            renderInput={(params) => <TextField {...params} />}
                          />
                        </Box>
                      )}
                    </>
                  )} */}

                  <Box>
                    <Typography fontSize={13} color="#555" mb={0.5}>
                      View Transactions
                    </Typography>
                    <Autocomplete
                      options={transactionRanges.map((r) => r.date)}
                      size="small"
                      disableClearable
                      value={viewTransactions}
                      onChange={(_, v) => onViewChange(v)}
                      renderInput={(p) => <TextField {...p} />}
                    />
                  </Box>

                  <FormControl fullWidth size="small">
                    <InputLabel id="restaurant-export-label">Export</InputLabel>
                    <Select
                      labelId="restaurant-export-label"
                      label="Export"
                      value={exportFormat}
                      onChange={(e) => setExportFormat(e.target.value)}
                      sx={{
                        bgcolor: "#f5f5f5",
                        fontWeight: 500,
                        fontSize: "13px",
                      }}
                    >
                      {exportOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* <Box>
                    <Typography fontSize={13} color="#555" mb={0.5}>
                      Date Range
                    </Typography>
                    <DateRangePicker
                      placeholder="From - To"
                      format="MM/dd/yyyy"
                      appearance="default"
                      value={dateRange}
                      onChange={onDateChange}
                      style={{ width: "100%" }}
                      container={() =>
                        document.getElementById("restaurant-dialog-content") ||
                        document.body
                      }
                      placement="right"
                    />
                  </Box> */}
                </Stack>
              </Box>
            </Box>

            <Box sx={{ width: "70%", height: "100%", overflow: "auto" }}>
              <ViewAndDisplayViewer
                onChange={setViewDisplayData}
                initialValues={saved}
                selectedCorporationCount={selectedCorporationCount}
              />
            </Box>
          </Stack>
        </DialogContent>

        <Loader loading={loading} />
      </Dialog>

      {corpDialog && (
        <RestaurantCorpSelectDialog
          open={corpDialog}
          onClose={() => setCorpDialog(false)}
          corporationData={restaurantCorps}
          selectedCorporations={selectedCorporations}
          restPortifolios={restPortifolios}
          onApply={(ids) => setSelectedCorporations(ids)}
        />
      )}
    </>
  );
};

export default RestuarentCustomization;
